/**
 * Caricamento di un pezzo di codice a richiesta, che sopravvive a un rilascio.
 *
 * ## Il problema, visto succedere
 *
 * Il codice e' spezzato in file col contenuto scritto nel nome -- `GraficoOperazioni-CzkewKfh.js`
 * -- cosi' il browser puo' tenerli in cache per sempre: se cambia il contenuto cambia il nome.
 * Il rovescio e' che a ogni rilascio i nomi vecchi smettono di esistere.
 *
 * Chi ha la pagina aperta durante un rilascio si trova con un documento che chiede file che non
 * ci sono piu'. Finche' naviga fra schermate gia' caricate non se ne accorge; appena apre
 * qualcosa che richiede un pezzo nuovo, il caricamento falla e l'applicazione muore con
 * «Failed to fetch dynamically imported module». E' accaduto sul dettaglio di un backtest
 * mentre il rilascio era in corso: il browser cercava `GraficoOperazioni-C0-xDRjy.js` e in
 * produzione c'era gia' `-CzkewKfh`.
 *
 * ## La cura, e perche' questa
 *
 * Al primo fallimento si ricarica la pagina una volta sola. Il documento nuovo porta con se' i
 * nomi nuovi, e la schermata si riapre dove era: e' l'unica riparazione possibile, perche' il
 * file richiesto non esiste piu' da nessuna parte e nessun tentativo aggiuntivo lo troverebbe.
 *
 * Una volta SOLA, ed e' la parte che conta: un ricaricamento automatico senza freno, davanti a
 * un errore che non dipende dalla versione -- una rete che cade, un file davvero rotto in
 * produzione -- diventerebbe un anello chiuso che ricarica all'infinito, molto peggio del
 * messaggio d'errore che sostituisce. Il segno sta in `sessionStorage`, quindi vive per quella
 * scheda e non oltre; se dopo il ricaricamento il caricamento falla ancora, l'errore passa e
 * viene mostrato, che a quel punto e' la cosa giusta.
 */

const CHIAVE = 'sb-ricaricato-per-modulo';

/**
 * Avvolge un `import()` con il recupero. Da usare al posto dell'import nudo dentro `lazy()`.
 *
 * ```ts
 * const Grafico = lazy(() => importoDinamico(() => import('./Grafico'), 'Grafico'));
 * ```
 */
export function importoDinamico<T>(carica: () => Promise<T>, nome: string): Promise<T> {
  return carica().catch((errore: unknown) => {
    let giaRicaricato = false;
    try {
      giaRicaricato = sessionStorage.getItem(CHIAVE) === nome;
    } catch {
      /* In una finestra privata, o con i dati del sito bloccati, `sessionStorage` solleva.
         Senza il segno non si puo' garantire "una volta sola", quindi si preferisce mostrare
         l'errore piuttosto che rischiare il ricaricamento a ripetizione. */
      throw errore;
    }

    if (giaRicaricato) {
      // Il ricaricamento e' gia' stato tentato: il problema non e' la versione.
      throw errore;
    }

    try {
      sessionStorage.setItem(CHIAVE, nome);
    } catch {
      throw errore;
    }

    // `reload()` non torna: la promessa resta in sospeso finche' il documento non e' sostituito,
    // e restituirla evita che nel frattempo compaia un errore che sta per essere superato.
    window.location.reload();
    return new Promise<T>(() => {});
  });
}

/** Da chiamare a caricamento riuscito: cosi' il permesso di ricaricare torna disponibile per un
 *  rilascio futuro, invece di essere speso per sempre al primo. */
export function importoRiuscito(): void {
  try {
    sessionStorage.removeItem(CHIAVE);
  } catch {
    /* Se non si puo' scrivere non si puo' nemmeno pulire: non e' un problema, il segno vive
       comunque solo per questa scheda. */
  }
}
